<template>
  <div
    class="stlLookStack"
    :class="{ 'is-stack': isStack, 'is-cycling': isCycling, 'is-cycle-active': isCycleActive }"
    @mouseenter="pauseCycle"
    @mouseleave="resumeCycle"
    @focusin="pauseCycle"
    @focusout="resumeCycle"
  >
    <div class="stlLookStackFrame">
      <div v-if="isStack" class="stlLookStackLayers" aria-hidden="true">
        <img
          v-for="layer in stackLayers"
          :key="layer.id"
          class="stlLookStackLayerImage"
          :src="layer.imageUrl"
          :alt="''"
          loading="lazy"
        >
      </div>

      <shop-the-look-item
        :config="activeLook"
        class="stlLookStackTop stlLookStackBase"
        @open-fullscreen="emitOpenFullscreen"
      />

      <div v-if="isCycling" class="stlLookStackTransition" aria-hidden="true">
        <picture v-if="cycleCurrentLook" class="stlLookStackTransitionPicture stlLookStackCurrentImage">
          <source v-if="cycleCurrentLook.imageUrlMobile" media="(max-width: 576px)" :srcset="cycleCurrentLook.imageUrlMobile">
          <img :src="cycleCurrentLook.imageUrl" alt="">
        </picture>

        <picture v-if="incomingLook" class="stlLookStackTransitionPicture stlLookStackIncomingImage">
          <source v-if="incomingLook.imageUrlMobile" media="(max-width: 576px)" :srcset="incomingLook.imageUrlMobile">
          <img :src="incomingLook.imageUrl" alt="">
        </picture>
      </div>

      <button
        v-if="isStack"
        type="button"
        class="stlLookStackCount"
        :aria-label="`Look mit ${variants.length} Varianten vergroessern`"
        @click.stop="emitOpenFullscreen"
      >
        +{{ variants.length - 1 }}
      </button>
    </div>

    <!-- eslint-disable-next-line vue/no-v-html -->
    <span class="caption" v-html="activeTitle" />
  </div>
</template>

<script>
import ShopTheLookItem from './ShopTheLookItem.vue'

export default {
  name: 'ShopTheLookStack',
  components: {
    ShopTheLookItem
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    autoCycle: {
      type: Boolean,
      default: true
    },
    autoCycleDelay: {
      type: Number,
      default: 1000
    },
    cycleTransitionDuration: {
      type: Number,
      default: 680
    }
  },
  data () {
    return {
      activeVariantId: this.getInitialVariantId(this.group),
      cycleCurrentVariantId: 0,
      incomingVariantId: 0,
      isCyclePreparing: false,
      isCycling: false,
      isCycleActive: false,
      isCyclePaused: false,
      hasCompletedInitialCycleDelay: false,
      cycleTimer: null,
      cycleTransitionTimer: null,
      reducedMotionQuery: null
    }
  },
  computed: {
    variants () {
      return this.buildVariants(this.group)
    },
    activeLook () {
      return this.findVariantById(this.activeVariantId) || this.variants[0] || this.group
    },
    incomingLook () {
      return this.findVariantById(this.incomingVariantId)
    },
    cycleCurrentLook () {
      return this.findVariantById(this.cycleCurrentVariantId)
    },
    activeTitle () {
      const titleLook = this.incomingLook && this.isCycleActive ? this.incomingLook : this.activeLook
      return titleLook.title || this.group.title || ''
    },
    isStack () {
      return this.variants.length > 1
    },
    stackLayers () {
      if (!this.isStack) {
        return []
      }

      return this.nextStackLayersForLook(this.activeLook)
    }
  },
  watch: {
    group: {
      deep: true,
      handler (nextGroup) {
        this.stopCycle()
        this.activeVariantId = this.getInitialVariantId(nextGroup)
        this.cycleCurrentVariantId = 0
        this.incomingVariantId = 0
        this.isCyclePreparing = false
        this.isCycling = false
        this.isCycleActive = false
        this.hasCompletedInitialCycleDelay = false

        this.$nextTick(() => {
          this.emitLayoutNeeded(false)
          this.scheduleCycle()
        })
      }
    }
  },
  mounted () {
    if (typeof window === 'undefined') {
      return
    }

    this.reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }

    this.scheduleCycle()
  },
  beforeDestroy () {
    this.stopCycle()

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
  },
  methods: {
    buildVariants (group) {
      const variants = []
      const seenIds = {}

      const addLook = (look) => {
        const id = Number(look && look.id)
        if (!look || id <= 0 || seenIds[id]) {
          return
        }

        seenIds[id] = true
        variants.push(look)
      }

      addLook(group)

      if (Array.isArray(group.children)) {
        group.children.forEach(addLook)
      }

      return variants
    },
    getInitialVariantId (group) {
      const variants = this.buildVariants(group)
      const selectedLookId = Number(group && group.selectedLookId)

      if (selectedLookId > 0 && variants.some(look => Number(look.id) === selectedLookId)) {
        return selectedLookId
      }

      const matchedLookIds = Array.isArray(group && group.matchedLookIds) ? group.matchedLookIds : []
      for (let i = 0; i < matchedLookIds.length; i++) {
        const matchedLookId = Number(matchedLookIds[i])
        if (matchedLookId > 0 && variants.some(look => Number(look.id) === matchedLookId)) {
          return matchedLookId
        }
      }

      return variants.length ? Number(variants[0].id) : 0
    },
    findVariantById (lookId) {
      const normalizedLookId = Number(lookId)
      return this.variants.find(look => Number(look.id) === normalizedLookId)
    },
    nextStackLayersForLook (activeLook) {
      const activeIndex = this.variants.findIndex(look => Number(look.id) === Number(activeLook && activeLook.id))
      const normalizedActiveIndex = activeIndex >= 0 ? activeIndex : 0

      return this.variants
        .slice(normalizedActiveIndex + 1)
        .concat(this.variants.slice(0, normalizedActiveIndex))
        .filter(look => look.imageUrl)
        .slice(0, 2)
    },
    selectVariant (lookId) {
      const variant = this.findVariantById(lookId)
      if (!variant) {
        return
      }

      this.activeVariantId = Number(variant.id)
      this.$nextTick(() => {
        this.emitLayoutNeeded(false)
      })
    },
    showNextVariant () {
      if (this.variants.length < 2) {
        return
      }

      const currentIndex = this.variants.findIndex(look => Number(look.id) === Number(this.activeVariantId))
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % this.variants.length : 0
      this.selectVariant(this.variants[nextIndex].id)
    },
    canCycle (force = false) {
      if ((!force && !this.autoCycle) || !this.isStack || this.isCyclePreparing || this.isCycling || this.isCyclePaused) {
        return false
      }

      if (this.reducedMotionQuery && this.reducedMotionQuery.matches) {
        return false
      }

      if (typeof document !== 'undefined' && document.hidden) {
        return false
      }

      return true
    },
    canBeTriggeredCycle () {
      return this.canCycle(true)
    },
    scheduleCycle () {
      this.stopCycleTimer()

      if (!this.canCycle()) {
        return
      }

      const delay = Math.max(1600, Number(this.autoCycleDelay) || 5200) + this.initialCycleStagger()
      this.cycleTimer = window.setTimeout(this.cycleToNextVariant, delay)
    },
    initialCycleStagger () {
      if (this.hasCompletedInitialCycleDelay) {
        return 0
      }

      const lookId = Number(this.group && this.group.id) || 0
      return (lookId % 6) * 450
    },
    stopCycleTimer () {
      if (this.cycleTimer) {
        window.clearTimeout(this.cycleTimer)
        this.cycleTimer = null
      }
    },
    stopCycleTransitionTimer () {
      if (this.cycleTransitionTimer) {
        window.clearTimeout(this.cycleTransitionTimer)
        this.cycleTransitionTimer = null
      }
    },
    stopCycle () {
      this.stopCycleTimer()
      this.stopCycleTransitionTimer()
    },
    async cycleToNextVariant (force = false) {
      if (!this.canCycle(force)) {
        if (!force) {
          this.scheduleCycle()
        }

        return
      }

      const currentIndex = this.variants.findIndex(look => Number(look.id) === Number(this.activeVariantId))
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % this.variants.length : 0
      const nextVariant = this.variants[nextIndex]

      if (!nextVariant) {
        if (!force) {
          this.scheduleCycle()
        }

        return
      }

      const currentVariant = this.activeLook

      this.isCyclePreparing = true
      await this.preloadLookImages([currentVariant, nextVariant])
      this.isCyclePreparing = false

      if (!this.canCycle(force)) {
        if (!force) {
          this.scheduleCycle()
        }

        return
      }

      this.cycleCurrentVariantId = Number(currentVariant.id)
      this.incomingVariantId = Number(nextVariant.id)
      this.isCycling = true
      this.isCycleActive = false
      this.stopCycleTransitionTimer()

      const transitionDuration = Math.max(250, Number(this.cycleTransitionDuration) || 680)

      await this.$nextTick()
      await this.nextAnimationFrame()
      await this.nextAnimationFrame()

      this.isCycleActive = true

      await new Promise(resolve => {
        this.cycleTransitionTimer = window.setTimeout(async () => {
          await this.finishCycle(nextVariant)
          resolve()
        }, transitionDuration)
      })
    },
    async finishCycle (nextVariant) {
      this.activeVariantId = Number(nextVariant.id)
      this.hasCompletedInitialCycleDelay = true
      this.cycleTransitionTimer = null

      await this.$nextTick()
      await this.waitForBaseImage()
      await this.nextAnimationFrame()
      await this.nextAnimationFrame()

      this.cycleCurrentVariantId = 0
      this.incomingVariantId = 0
      this.isCycling = false
      this.isCycleActive = false

      this.$nextTick(() => {
        this.emitLayoutNeeded(false)
        this.scheduleCycle()
      })
    },
    imageSourcesForLook (look) {
      if (!look) {
        return []
      }

      return [look.imageUrl, look.imageUrlMobile].filter(Boolean)
    },
    preloadLookImages (looks) {
      if (typeof window === 'undefined' || typeof window.Image === 'undefined') {
        return Promise.resolve()
      }

      const seenSources = {}
      const sources = []

      looks.forEach(look => {
        this.imageSourcesForLook(look).forEach(source => {
          if (!seenSources[source]) {
            seenSources[source] = true
            sources.push(source)
          }
        })
      })

      if (!sources.length) {
        return Promise.resolve()
      }

      return Promise.all(sources.map(source => new Promise(resolve => {
        const image = new window.Image()
        const finish = () => resolve()

        image.onload = finish
        image.onerror = finish
        image.src = source

        if (image.complete) {
          finish()
        }
      })))
    },
    waitForBaseImage () {
      if (typeof window === 'undefined' || !this.$el) {
        return Promise.resolve()
      }

      const image = this.$el.querySelector('.stlLookStackBase img')
      if (!image || image.complete) {
        return Promise.resolve()
      }

      return new Promise(resolve => {
        const finish = () => {
          image.removeEventListener('load', finish)
          image.removeEventListener('error', finish)
          resolve()
        }

        image.addEventListener('load', finish)
        image.addEventListener('error', finish)
      })
    },
    nextAnimationFrame () {
      return new Promise(resolve => {
        window.requestAnimationFrame(resolve)
      })
    },
    pauseCycle () {
      this.isCyclePaused = true
      this.stopCycleTimer()
    },
    resumeCycle () {
      this.isCyclePaused = false
      this.scheduleCycle()
    },
    onVisibilityChange () {
      if (typeof document !== 'undefined' && document.hidden) {
        this.stopCycleTimer()
        return
      }

      this.scheduleCycle()
    },
    emitOpenFullscreen () {
      this.$emit('open-fullscreen', Number(this.activeLook.id))
    },
    emitLayoutNeeded (waitForImages = false) {
      this.$emit('layout-needed', waitForImages)
    }
  }
}
</script>
